import React, { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router";
import { useSelectedTheme, useTranslate } from "../../../../Hooks";
import { Box, Typography } from "@material-ui/core";
import { useState } from "react";
import { AgentRotationRangeTypeEnum } from "../../../../Enums";
import {
  UpdateRotationScheme,
  GetRotationSchemeByIdServices,
} from "../../../../Services";
import { useVerticalNav } from "../../../../Contexts/VerticalNavContext";
import {
  CustomBadge,
  CustomBreadCrumb,
  CustomButton,
  CustomIconButton,
  FormSkeleton,
  PageTitle,
  TabsComponent,
} from "../../../../Components";
import EditSchemaForm from "./EditSchemaForm/EditSchemaForm";
import EditSchemaSettingsForm from "./EditSchemaSettingsForm/EditSchemaSettingsForm";
import { buildEditSchemaQueryBody } from "../util";

import useStyles from "./styles";

import { ArrowLeftIcon } from "../../../../assets/icons";

function NewEditAgentsRotationCriteriaView() {
  const [activeTab, setActiveTab] = useState(0);
  const [formValues, setFormValues] = useState({
    addRotationForm: {},
    schemaSettingsForm: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useHistory();
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const { setAlertBoxContent } = useVerticalNav();

  const location = useLocation();
  const schemaId = new URLSearchParams(location.search).get("id");
  const rotationFormikRef = useRef();
  const schemaSettingsFormikRef = useRef();
  const {
    theme: { palette },
  } = useSelectedTheme();

  const goBackHandler = () => {
    navigate.goBack();
  };
  const getRangeValue = (rangeList, name) => {
    const range = rangeList.find(
      ({ agentRotationRangeTypeId }) =>
        AgentRotationRangeTypeEnum[name].key === agentRotationRangeTypeId
    );
    if (range) {
      return [range.startValue, range.endValue];
    }
    return [];
  };
  const getSchemaDetails = async () => {
    const res = await GetRotationSchemeByIdServices(schemaId);
    if (!(res && res.status && res.status !== 200)) {
      setFormValues({
        addRotationForm: {
          isActive: res.isActive,
          label: res.label,
          order: res.order,
          buyer: !!res.leadsType.find(({ leadClass }) => leadClass === "Buyer"),
          tenant: !!res.leadsType.find(
            ({ leadClass }) => leadClass === "Tenant"
          ),
          vip: !!res.leadClasses.find(
            ({ lookupItemName }) => lookupItemName === "VIP"
          ),
          vvip: !!res.leadClasses.find(
            ({ lookupItemName }) => lookupItemName === "VVIP"
          ),
          normal: !!res.leadClasses.find(
            ({ lookupItemName }) => lookupItemName === "Normal"
          ),
          isBulk: res.isBulk,
          rotationSchemaReferredBys: res.referredBys,
          mediaDetails: res.medias,
          developer: res.developers,
          unitType: res.unitTypes,
          priceRange: getRangeValue(res.rotationSchemeRanges, "PriceRange"),
          size: getRangeValue(res.rotationSchemeRanges, "Size"),
          bedrooms: getRangeValue(res.rotationSchemeRanges, "Bedroom"),
          bathrooms: getRangeValue(res.rotationSchemeRanges, "Bathroom"),
          propertyCampaignIds: [],
          rotationPreferredLanguages: res.languages,
          rotationSchemeCities: res.cities,
          rotationSchemeCommunities: res.communities,
          rotationSchemeCountries: res.countries,
          rotationSchemeDistricts: res.districts,
          rotationSchemeMedias: res.medias,
          rotationSchemeProperties: res.properties,
          rotationSchemeSubCommunities: res.subCommunities,
          rotationSchemeUnitTypes: res.unitTypes,
          rotationSchemaMethodOfContacts: res.methodOfContact,
          rotationSchemeCampaigns: res.rotationSchemeCampaigns,
        },
        schemaSettingsForm: {
          isOverrideLeadCapacitySettingEnabled:
            res.isOverrideLeadCapacitySettingEnabled,
          rotationSchemeLeadDistributionType:
            res.rotationSchemeLeadDistributionType,
          sla: res.sla,
          rotationSchemeBranches: res.rotationSchemeBranches,
          rotationSchemeTeams: res.rotationSchemeTeams,
          rotationSchemeCourses: res.rotationSchemeCourses,
        },
      });
      setIsLoading(false);
    }
  };

  const saveSchema = async () => {
    const queryValues = { ...formValues };
    if (rotationFormikRef.current) {
      queryValues.addRotationForm = {
        ...formValues.addRotationForm,
        ...rotationFormikRef.current?.values,
      };
    }
    if (schemaSettingsFormikRef.current) {
      queryValues.schemaSettingsForm = {
        ...formValues.schemaSettingsForm,
        ...schemaSettingsFormikRef.current?.values,
      };
    }
    const body = buildEditSchemaQueryBody(queryValues, schemaId);
    const res = await UpdateRotationScheme(schemaId, body);
    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate(`create-schema-modal-title`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } else {
      const errorMessage = (res && res.data && res.data.Message).split(": ")[1];
      if (errorMessage === "___DUPLICATE_ROTATION_SCHEME_ORDER___")
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: "Failed to Create Schema.",
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      else
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("Shared:Server-Error"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
    }
  };

  const formSubTitle = [
    {
      text: translate("rotation-criteria"),
      subText: translate("edit-schema-page-tab1-subtext"),
    },
    {
      text: translate("schema-setting"),
      subText: translate("edit-schema-page-tab2-subtext"),
    },
  ];

  const styles = useStyles();

  useEffect(() => {
    getSchemaDetails();
  }, []);

  return (
    <>
      <CustomBreadCrumb
        breadcrumbs={[
          {
            label: translate("agent-management"),
            nonClickable: true,
          },
          {
            label: translate("rotation-schema"),
            nonClickable: true,
          },
        ]}
        containerClasses={styles.breadCrumbContainer}
      >
        <CustomBadge
          Style={{
            padding: "4px 8px",
            borderRadius: "6px",
            lineHeight: "20px",
          }}
          label={formValues.addRotationForm?.label || ""}
          BackgroundColor={palette.breadcrumbs.brand_bg_hover}
          BorderColor={palette.breadcrumbs.brand_bg_hover}
          Color={palette.breadcrumbs.brand_fg_hover}
        />
      </CustomBreadCrumb>
      <Box className={styles.mainHeading}>
        <CustomIconButton
          variant="text"
          size="none"
          boxShadow="none"
          color="tertiaryColor"
          hideHoverBg
          onClick={goBackHandler}
        >
          <ArrowLeftIcon
            width="20"
            height="20"
            fill={palette.button.tertiary_fg}
          />
        </CustomIconButton>
        <Box>
          <PageTitle
            title={translate("edit-rotation-schema")}
            subTitle={translate("edit-rotation-subtitle")}
            wrapperClasses={styles.pageTitle}
            hideDivider
          />
        </Box>
      </Box>
      <Box className={styles.contentWrapper}>
        <Box className={styles.headingContainer}>
          {/* side menu */}
          <Box>
            <TabsComponent
              data={[
                {
                  tab: translate("rotation-criteria"),
                },
                {
                  tab: translate("schema-setting"),
                },
              ]}
              labelInput="tab"
              parentTranslationPath={""}
              translationPath={""}
              themeClasses="theme-curved"
              currentTab={activeTab}
              onTabChanged={(_, tab) => {
                if (rotationFormikRef.current)
                  setFormValues({
                    ...formValues,
                    addRotationForm: {
                      ...formValues.addRotationForm,
                      ...rotationFormikRef.current?.values,
                    },
                  });
                if (schemaSettingsFormikRef.current)
                  setFormValues({
                    ...formValues,
                    schemaSettingsForm: {
                      ...formValues.schemaSettingsForm,
                      ...schemaSettingsFormikRef.current?.values,
                    },
                  });
                setActiveTab(tab);
              }}
              orientation={"vertical"}
              reskinningWrapperStyles={styles.wrapperTabsRoot}
            />
          </Box>
        </Box>
        <Box width={"100%"}>
          <Box className={styles.subTitleWrapper}>
            <Box>
              <Box className={styles.subtitle}>
                <Typography variant="h6" component="h2">
                  {formSubTitle[activeTab].text}
                </Typography>
              </Box>
              <p className={styles.subText}>
                {formSubTitle[activeTab].subText}
              </p>
            </Box>
            <Box className={styles.actionContainer}>
              <CustomButton
                boxShadow="xs"
                size="md"
                variant={"outlined"}
                color={"secondary"}
                onClick={goBackHandler}
              >
                {translate("discard")}
              </CustomButton>
              <CustomButton
                boxShadow="xs"
                size="md"
                variant={"secondary"}
                color={"primary"}
                onClick={saveSchema}
              >
                {translate("save")}
              </CustomButton>
            </Box>
          </Box>
          {isLoading && <FormSkeleton />}
          {activeTab === 0 && !isLoading && (
            <EditSchemaForm
              formRef={rotationFormikRef}
              schemaDetails={{
                ...formValues.addRotationForm,
              }}
            />
          )}
          {activeTab === 1 && !isLoading && (
            <EditSchemaSettingsForm
              formRef={schemaSettingsFormikRef}
              schemaDetails={{
                ...formValues.schemaSettingsForm,
              }}
            />
          )}
        </Box>
      </Box>
    </>
  );
}

export default NewEditAgentsRotationCriteriaView;
